import { useCallback, useEffect, useState } from "react";
import type { DoorSignProjection } from "@shared/types/foyer";
import { getBinding, getScreenId } from "../lib/binding";
import { eventBus } from "../lib/eventBus";

/**
 * Poll cadence for the door-sign projection. A sign must reflect a status change
 * (e.g. "Do not disturb") or an admit/discharge within a few seconds to be
 * useful, but the projection is a cheap, PHI-free read, so 15s balances freshness
 * against load. (A realtime nudge is a follow-up; see docs/door-sign-spec.md §3.)
 */
const POLL_MS = 15_000;

/**
 * Fetches this screen's door-sign projection ({ occupied, status, displayName,
 * … }) from the screen-token-gated endpoint and keeps it fresh. The projection
 * is already PHI-reduced server-side, so this decrypts nothing. Inactive (e.g.
 * design preview) → no fetch.
 */
export function useDoorSign(active: boolean): {
  projection: DoorSignProjection | null;
  error: string | null;
} {
  const [projection, setProjection] = useState<DoorSignProjection | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchProjection = useCallback(async () => {
    const binding = getBinding();
    if (!binding?.screenToken) return;
    const screenId = getScreenId();
    try {
      const res = await fetch(`/api/foyer/screens/${screenId}/door-sign`, {
        headers: { "X-Screen-Token": binding.screenToken },
      });
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      setProjection((await res.json()) as DoorSignProjection);
      setError(null);
    } catch (err) {
      // Keep showing the last good projection on a blip; just record the error.
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    if (!active) return;
    void fetchProjection();
    const t = window.setInterval(() => void fetchProjection(), POLL_MS);
    // Realtime nudge: the server publishes `door_sign_changed` (room-scoped) on
    // an admit/discharge/status/policy change, so the sign updates within a
    // second instead of waiting up to POLL_MS. Polling stays as the backstop.
    const off = eventBus.on((evt) => {
      if (evt.type === "door_sign_changed") void fetchProjection();
    });
    return () => {
      window.clearInterval(t);
      off();
    };
  }, [active, fetchProjection]);

  return { projection, error };
}
